import twilioService from './twilio-service';
import { supabase } from './supabase';

/**
 * Helper functions for creating and managing conversations
 */

/**
 * Start a new SMS conversation with a client
 * This will create a conversation and assign the best local Twilio number
 */
export async function startConversationWithClient(clientId, agentId) {
  try {
    // Get client details
    const { data: client, error: clientError } = await supabase
      .from('clients')
      .select('id, name, phone, email')
      .eq('id', clientId)
      .single();

    if (clientError || !client) {
      return { 
        success: false, 
        error: 'Client not found' 
      };
    }

    if (!client.phone) {
      return { 
        success: false, 
        error: 'Client has no phone number' 
      };
    }

    // Check if conversation already exists
    const { data: existing } = await supabase
      .from('conversations')
      .select('id')
      .eq('client_id', clientId)
      .eq('agent_id', agentId)
      .eq('channel', 'sms')
      .single();

    if (existing) {
      return {
        success: true,
        conversationId: existing.id,
        message: 'Conversation already exists',
        existing: true
      };
    }

    // Create new conversation with local number matching
    const { data: conversation, error: convError } = 
      await twilioService.getOrCreateConversation(clientId, agentId, client.phone);

    if (convError || !conversation) {
      return { 
        success: false, 
        error: 'Failed to create conversation' 
      };
    }

    return {
      success: true,
      conversationId: conversation.id,
      twilioPhone: conversation.twilio_phone,
      clientPhone: conversation.client_phone,
      message: 'Conversation created successfully',
      existing: false
    };

  } catch (err) {
    console.error('Error starting conversation:', err);
    return { 
      success: false, 
      error: err.message || 'An error occurred' 
    };
  }
}

/**
 * Send a quick SMS to a client (creates conversation if needed)
 */
export async function sendQuickSMS(clientId, agentId, message) {
  try {
    // Start or get existing conversation
    const convResult = await startConversationWithClient(clientId, agentId);
    
    if (!convResult.success) {
      return convResult;
    }

    // Get client phone
    const { data: client } = await supabase
      .from('clients')
      .select('phone')
      .eq('id', clientId)
      .single();

    if (!client?.phone) {
      return { 
        success: false, 
        error: 'Client has no phone number' 
      };
    }

    // Send the message
    const result = await twilioService.sendSMS({
      conversationId: convResult.conversationId,
      clientId,
      agentId,
      toPhone: client.phone,
      message
    });

    return result;

  } catch (err) {
    console.error('Error sending quick SMS:', err);
    return { 
      success: false, 
      error: err.message || 'Failed to send SMS' 
    };
  }
}

/**
 * Get or create conversation and navigate to it
 * Use this in your client detail modal or client management page
 */
export async function openConversation(clientId, agentId, navigate) {
  const result = await startConversationWithClient(clientId, agentId);
  
  if (result.success) {
    // Navigate to conversations page
    // You can modify this based on your routing setup
    navigate('/conversations', { 
      state: { 
        conversationId: result.conversationId 
      }
    });
  }
  
  return result;
}

/**
 * Check if client can receive SMS
 */
export function canClientReceiveSMS(client) {
  if (!client?.phone) return false;
  
  // Basic US phone number validation
  const phoneDigits = client.phone.replace(/[^0-9]/g, '');
  return phoneDigits.length === 10 || phoneDigits.length === 11;
}

/**
 * Format phone number for display
 */
export function formatPhoneNumber(phone) {
  if (!phone) return '';
  
  const digits = phone.replace(/[^0-9]/g, '');
  
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  } else if (digits.length === 11 && digits[0] === '1') {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  
  return phone;
}

/**
 * Get area code from phone number
 */
export function getAreaCode(phone) {
  if (!phone) return null;
  
  const digits = phone.replace(/[^0-9]/g, '');
  
  if (digits.length === 11 && digits[0] === '1') {
    return digits.slice(1, 4);
  } else if (digits.length === 10) {
    return digits.slice(0, 3);
  }
  
  return null;
}

export default {
  startConversationWithClient,
  sendQuickSMS,
  openConversation,
  canClientReceiveSMS,
  formatPhoneNumber,
  getAreaCode
};

